import React, { useState } from 'react';
import {
  Layers,
  Users,
  Eye,
  Target,
  MessageSquare,
  Download,
  FileText,
  Presentation,
  CreditCard,
  EyeOff,
  Lock,
  Search
} from 'lucide-react';

export const ArtifactGallery: React.FC = () => {
  const [selectedTool, setSelectedTool] = useState<'ladder' | 'johari' | 'logical'>('ladder');
  const [selectedFormat, setSelectedFormat] = useState<'field-card' | 'slide' | 'pdf'>('field-card');

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'bg-consulting-slate-blue-100 border-consulting-slate-blue-300 text-consulting-slate-blue-800';
      case 'secondary':
        return 'bg-sage-100 border-sage-300 text-sage-800';
      case 'accent':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'coral':
        return 'bg-luminous-coral-100 border-luminous-coral-300 text-luminous-coral-800';
      default:
        return 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const renderLadderFieldCard = () => (
    <div className="w-80 h-48 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-consulting-slate-blue-600" />
          <h3 className="font-bold text-sm text-charcoal-900">Ladder of Inference</h3>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-sage-500 rounded-full" title="Ready"></div>
          <div className="w-2 h-2 bg-[var(--mustard)] rounded-full" title="High Learning"></div>
        </div>
      </div>

      {/* Essential Phrase with Encoding */}
      <div className="mb-3 p-2 bg-[var(--mustard-dim)] rounded border-2 border-[var(--mustard)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm">💡</span>
          <span className="text-xs font-medium text-[var(--mustard)]">Important Tip</span>
        </div>
        <p className="text-xs font-medium text-[var(--mustard)] text-center">
          "How little data he's drawing massive conclusions from"
        </p>
      </div>

      {/* Compact Ladder - Just the Triangle Structure */}
      <div className="space-y-1 mb-3">
        {[
          { id: 1, title: 'Data', color: 'primary' },
          { id: 2, title: 'Select', color: 'primary' },
          { id: 3, title: 'Interpret', color: 'secondary' },
          { id: 4, title: 'Assume', color: 'secondary' },
          { id: 5, title: 'Conclude', color: 'accent' },
          { id: 6, title: 'Believe', color: 'accent' },
          { id: 7, title: 'Act', color: 'accent' }
        ].map((rung) => (
          <div key={rung.id} className={`h-4 rounded border-l-2 ${getColorClasses(rung.color)} flex items-center px-2`}>
            <span className="text-xs font-medium">{rung.id}. {rung.title}</span>
          </div>
        ))}
      </div>

      {/* Quick Reference with Encoding */}
      <div className="space-y-2">
        <div className="text-xs text-center text-charcoal-600">
          <span className="font-medium">Key Question:</span> "What data am I actually seeing?"
        </div>
        <div className="flex items-center justify-center gap-2">
          <div className="px-2 py-1 bg-sage-50 rounded border border-sage-300 text-xs text-sage-800">
            📚 Self-Knowledge Deck
          </div>
          <div className="px-2 py-1 bg-consulting-slate-blue-50 rounded border border-consulting-slate-blue-300 text-xs text-consulting-slate-blue-800">
            📖 See: Johari Window
          </div>
        </div>
      </div>
    </div>
  );

  const renderLadderSlide = () => (
    <div className="w-full h-96 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Layers className="h-8 w-8 text-consulting-slate-blue-600" />
          <h1 className="text-2xl font-bold text-charcoal-900">The Ladder of Inference</h1>
        </div>
        <p className="text-sm text-charcoal-600">A framework for understanding how we move from data to action</p>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">The Framework</h3>
          <div className="space-y-2">
            {[
              { id: 1, title: 'Observable Data', description: 'Facts and events we can observe', color: 'primary' },
              { id: 2, title: 'Selected Data', description: 'What we choose to notice', color: 'primary' },
              { id: 3, title: 'Interpreted Meaning', description: 'What we think it means', color: 'secondary' },
              { id: 4, title: 'Assumptions Made', description: 'Beliefs we bring to the situation', color: 'secondary' },
              { id: 5, title: 'Conclusions Drawn', description: 'Judgments we make', color: 'accent' },
              { id: 6, title: 'Beliefs Formed', description: 'What we come to believe', color: 'accent' },
              { id: 7, title: 'Actions Taken', description: 'What we do as a result', color: 'accent' }
            ].map((rung) => (
              <div key={rung.id} className={`p-2 rounded border-l-4 ${getColorClasses(rung.color)}`}>
                <div className="text-sm font-medium">{rung.id}. {rung.title}</div>
                <div className="text-xs opacity-75">{rung.description}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Insights</h3>
          <div className="space-y-3">
            <div className="p-3 bg-sage-50 border border-sage-200">
              <div className="text-sm font-medium text-sage-800">Reflection Requirement</div>
              <div className="text-xs text-sage-700">Always question assumptions before acting</div>
            </div>
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="text-sm font-medium text-[var(--mustard)]">High Learning Value</div>
              <div className="text-xs text-[var(--mustard)]">Teaches systematic thinking patterns</div>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="text-sm font-medium text-charcoal-900 mb-2">Visual Signatures</h4>
            <div className="flex items-center gap-2">
              <div className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs flex items-center gap-1">
                <Layers className="h-3 w-3" />
                <span>Framework</span>
              </div>
              <div className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-xs flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span>Coaching</span>
              </div>
              <div className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs flex items-center gap-1">
                <Target className="h-3 w-3" />
                <span>Individual</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLadderPDF = () => (
    <div className="w-full h-[800px] bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg overflow-y-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Layers className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-3xl font-bold text-charcoal-900">Ladder of Inference</h1>
        </div>
        <p className="text-lg text-charcoal-600">A Framework for Better Thinking and Decision-Making</p>
        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="px-3 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded-full text-sm flex items-center gap-1">
            <Layers className="h-4 w-4" />
            <span>Framework</span>
          </div>
          <div className="px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-sm flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span>Coaching</span>
          </div>
          <div className="px-3 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-sm flex items-center gap-1">
            <Target className="h-4 w-4" />
            <span>Individual</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">The Framework</h2>
          <div className="space-y-3">
            {[
              { id: 1, title: 'Observable Data', description: 'Facts and events we can observe', color: 'primary' },
              { id: 2, title: 'Selected Data', description: 'What we choose to notice', color: 'primary' },
              { id: 3, title: 'Interpreted Meaning', description: 'What we think it means', color: 'secondary' },
              { id: 4, title: 'Assumptions Made', description: 'Beliefs we bring to the situation', color: 'secondary' },
              { id: 5, title: 'Conclusions Drawn', description: 'Judgments we make', color: 'accent' },
              { id: 6, title: 'Beliefs Formed', description: 'What we come to believe', color: 'accent' },
              { id: 7, title: 'Actions Taken', description: 'What we do as a result', color: 'accent' }
            ].map((rung) => (
              <div key={rung.id} className={`p-4  border-2 ${getColorClasses(rung.color)}`}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[var(--bg-raised)] border-2 border-charcoal-600 rounded-full flex items-center justify-center text-xs font-bold text-charcoal-600">
                    {rung.id}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{rung.title}</div>
                    <div className="text-xs opacity-75">{rung.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">M3 Metadata</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Categories</h3>
              <div className="flex flex-wrap gap-1">
                {['Framework', 'Method', 'Tool'].map((category) => (
                  <span key={category} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                    {category}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Attributes</h3>
              <div className="space-y-2">
                {[
                  { label: 'Impact/Value', value: 8, max: 10 },
                  { label: 'Learning Value', value: 9, max: 10 },
                  { label: 'Clarity/Ambiguity', value: 7, max: 10 }
                ].map((attr) => (
                  <div key={attr.label} className="flex items-center gap-2">
                    <span className="text-xs w-20">{attr.label}</span>
                    <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2">
                      <div 
                        className="bg-consulting-slate-blue-600 h-2 rounded-full"
                        style={{ width: `${(attr.value / attr.max) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs w-4">{attr.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">When to Use</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• Understanding decision-making processes</li>
              <li>• Identifying assumptions and biases</li>
              <li>• Improving communication and feedback</li>
              <li>• Coaching and self-reflection</li>
            </ul>
          </div>
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Questions</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• What data am I actually seeing?</li>
              <li>• What assumptions am I making?</li>
              <li>• How did I get from data to conclusion?</li>
              <li>• What other interpretations are possible?</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderJohariFieldCard = () => (
    <div className="w-80 h-48 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-consulting-slate-blue-600" />
          <h3 className="font-bold text-sm text-charcoal-900">Johari Window</h3>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-sage-500 rounded-full" title="Ready"></div>
          <div className="w-2 h-2 bg-[var(--mustard)] rounded-full" title="High Learning"></div>
        </div>
      </div>

      {/* Essential Phrase with Encoding */}
      <div className="mb-3 p-2 bg-[var(--mustard-dim)] rounded border-2 border-[var(--mustard)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm">💡</span>
          <span className="text-xs font-medium text-[var(--mustard)]">Important Tip</span>
        </div>
        <p className="text-xs font-medium text-[var(--mustard)] text-center">
          "Things people don't know about you - strategic disclosure"
        </p>
      </div>

      {/* Compact Johari Window - Just the Matrix Structure */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className={`p-2 rounded border-l-4 ${getColorClasses('primary')} text-center`}>
          <div className="text-xs font-medium">Open</div>
          <div className="text-xs opacity-75">Known to all</div>
        </div>
        <div className={`p-2 rounded border-l-4 ${getColorClasses('secondary')} text-center`}>
          <div className="text-xs font-medium">Blind</div>
          <div className="text-xs opacity-75">Others see</div>
        </div>
        <div className={`p-2 rounded border-l-4 ${getColorClasses('accent')} text-center`}>
          <div className="text-xs font-medium">Hidden</div>
          <div className="text-xs opacity-75">You know</div>
        </div>
        <div className={`p-2 rounded border-l-4 ${getColorClasses('coral')} text-center`}>
          <div className="text-xs font-medium">Unknown</div>
          <div className="text-xs opacity-75">No one knows</div>
        </div>
      </div>

      {/* Quick Reference with Encoding */}
      <div className="space-y-2">
        <div className="text-xs text-center text-charcoal-600">
          <span className="font-medium">Key Question:</span> "What do others see that I don't?"
        </div>
        <div className="flex items-center justify-center gap-2">
          <div className="px-2 py-1 bg-sage-50 rounded border border-sage-300 text-xs text-sage-800">
            📚 Self-Knowledge Deck
          </div>
          <div className="px-2 py-1 bg-sage-50 rounded border-dashed border-sage-300 text-xs text-sage-800">
            🔗 2x2 Matrix
          </div>
        </div>
      </div>
    </div>
  );

  const renderJohariSlide = () => (
    <div className="w-full h-96 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Users className="h-8 w-8 text-consulting-slate-blue-600" />
          <h1 className="text-2xl font-bold text-charcoal-900">The Johari Window</h1>
        </div>
        <p className="text-sm text-charcoal-600">A framework for understanding self-awareness and self-disclosure</p>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">The Framework</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4  border-2 ${getColorClasses('primary')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Eye className="h-5 w-5" />
                <span className="font-semibold text-sm">Open Self</span>
              </div>
              <div className="text-xs opacity-75">
                Known to self<br />
                Known to others
              </div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('secondary')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <EyeOff className="h-5 w-5" />
                <span className="font-semibold text-sm">Blind Self</span>
              </div>
              <div className="text-xs opacity-75">
                Unknown to self<br />
                Known to others
              </div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('accent')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Lock className="h-5 w-5" />
                <span className="font-semibold text-sm">Hidden Self</span>
              </div>
              <div className="text-xs opacity-75">
                Known to self<br />
                Unknown to others
              </div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('coral')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Search className="h-5 w-5" />
                <span className="font-semibold text-sm">Unknown Self</span>
              </div>
              <div className="text-xs opacity-75">
                Unknown to self<br />
                Unknown to others
              </div>
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Insights</h3>
          <div className="space-y-3">
            <div className="p-3 bg-sage-50 border border-sage-200">
              <div className="text-sm font-medium text-sage-800">Feedback Requirement</div>
              <div className="text-xs text-sage-700">Ask others what they see in you</div>
            </div>
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="text-sm font-medium text-[var(--mustard)]">High Learning Value</div>
              <div className="text-xs text-[var(--mustard)]">Reveals blind spots and hidden strengths</div>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="text-sm font-medium text-charcoal-900 mb-2">Visual Signatures</h4>
            <div className="flex items-center gap-2">
              <div className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs flex items-center gap-1">
                <Layers className="h-3 w-3" />
                <span>Framework</span>
              </div>
              <div className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-xs flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span>Coaching</span>
              </div>
              <div className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs flex items-center gap-1">
                <Target className="h-3 w-3" />
                <span>Individual</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderJohariPDF = () => (
    <div className="w-full h-[800px] bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg overflow-y-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Users className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-3xl font-bold text-charcoal-900">Johari Window</h1>
        </div>
        <p className="text-lg text-charcoal-600">Know and Share Yourself With Skill</p>
        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="px-3 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded-full text-sm flex items-center gap-1">
            <Layers className="h-4 w-4" />
            <span>Framework</span>
          </div>
          <div className="px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-sm flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span>Coaching</span>
          </div>
          <div className="px-3 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-sm flex items-center gap-1">
            <Target className="h-4 w-4" />
            <span>Individual</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">The Framework</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4  border-2 ${getColorClasses('primary')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Eye className="h-5 w-5" />
                <span className="font-semibold text-sm">Open Self</span>
              </div>
              <div className="text-xs opacity-75 mb-2">
                Known to self<br />
                Known to others
              </div>
              <div className="text-xs font-medium">Arena</div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('secondary')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <EyeOff className="h-5 w-5" />
                <span className="font-semibold text-sm">Blind Self</span>
              </div>
              <div className="text-xs opacity-75 mb-2">
                Unknown to self<br />
                Known to others
              </div>
              <div className="text-xs font-medium">Blind Spot</div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('accent')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Lock className="h-5 w-5" />
                <span className="font-semibold text-sm">Hidden Self</span>
              </div>
              <div className="text-xs opacity-75 mb-2">
                Known to self<br />
                Unknown to others
              </div>
              <div className="text-xs font-medium">Facade</div>
            </div>
            <div className={`p-4  border-2 ${getColorClasses('coral')} text-center`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Search className="h-5 w-5" />
                <span className="font-semibold text-sm">Unknown Self</span>
              </div>
              <div className="text-xs opacity-75 mb-2">
                Unknown to self<br />
                Unknown to others
              </div>
              <div className="text-xs font-medium">Unknown</div>
            </div>
          </div>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">M3 Metadata</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Categories</h3>
              <div className="flex flex-wrap gap-1">
                {['Framework', 'Tool', 'Method'].map((category) => (
                  <span key={category} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                    {category}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Attributes</h3>
              <div className="space-y-2">
                {[
                  { label: 'Impact/Value', value: 7, max: 10 },
                  { label: 'Learning Value', value: 8, max: 10 },
                  { label: 'Clarity/Ambiguity', value: 8, max: 10 }
                ].map((attr) => (
                  <div key={attr.label} className="flex items-center gap-2">
                    <span className="text-xs w-20">{attr.label}</span>
                    <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2">
                      <div 
                        className="bg-consulting-slate-blue-600 h-2 rounded-full"
                        style={{ width: `${(attr.value / attr.max) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs w-4">{attr.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">When to Use</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• Understanding self-awareness gaps</li>
              <li>• Improving communication and feedback</li>
              <li>• Building trust and relationships</li>
              <li>• Team building and development</li>
            </ul>
          </div>
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Questions</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• What do others see that I don't?</li>
              <li>• What am I hiding that could help?</li>
              <li>• How can I expand my open area?</li>
              <li>• What feedback do I need to seek?</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLogicalFieldCard = () => (
    <div className="w-80 h-48 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-consulting-slate-blue-600" />
          <h3 className="font-bold text-sm text-charcoal-900">Logical Levels</h3>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-sage-500 rounded-full" title="Ready"></div>
          <div className="w-2 h-2 bg-[var(--mustard)] rounded-full" title="High Learning"></div>
        </div>
      </div>

      {/* Essential Phrase with Encoding */}
      <div className="mb-3 p-2 bg-[var(--mustard-dim)] rounded border-2 border-[var(--mustard)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm">💡</span>
          <span className="text-xs font-medium text-[var(--mustard)]">Important Tip</span>
        </div>
        <p className="text-xs font-medium text-[var(--mustard)] text-center">
          "Way to tell a story - who you are, what you value, what you're good at"
        </p>
      </div>

      {/* Compact Levels - Just the Triangle Structure */}
      <div className="space-y-1 mb-3">
        {[
          { id: 1, title: 'Spirituality', color: 'primary' },
          { id: 2, title: 'Identity', color: 'primary' },
          { id: 3, title: 'Values', color: 'secondary' },
          { id: 4, title: 'Skills', color: 'secondary' },
          { id: 5, title: 'Behavior', color: 'accent' },
          { id: 6, title: 'Environment', color: 'accent' }
        ].map((level) => (
          <div key={level.id} className={`h-4 rounded border-l-2 ${getColorClasses(level.color)} flex items-center px-2`}>
            <span className="text-xs font-medium">{level.id}. {level.title}</span>
          </div>
        ))}
      </div>

      {/* Quick Reference with Encoding */}
      <div className="space-y-2">
        <div className="text-xs text-center text-charcoal-600">
          <span className="font-medium">Key Question:</span> "At what level is the real issue?"
        </div>
        <div className="flex items-center justify-center gap-2">
          <div className="px-2 py-1 bg-sage-50 rounded border border-sage-300 text-xs text-sage-800">
            📚 Self-Knowledge Deck
          </div>
          <div className="px-2 py-1 bg-sage-50 rounded border-dashed border-sage-300 text-xs text-sage-800">
            🔗 Hierarchical List
          </div>
          <div className="px-2 py-1 bg-coral-50 rounded border-dotted border-coral-300 text-xs text-coral-800">
            🧭 Context Matters
          </div>
        </div>
      </div>
    </div>
  );

  const renderLogicalSlide = () => (
    <div className="w-full h-96 bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Layers className="h-8 w-8 text-consulting-slate-blue-600" />
          <h1 className="text-2xl font-bold text-charcoal-900">Logical Levels of Change</h1>
        </div>
        <p className="text-sm text-charcoal-600">A hierarchical framework for understanding change at different levels</p>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">The Framework</h3>
          <div className="space-y-2">
            {[
              { id: 1, title: 'Spirituality', subtitle: 'Mission/Purpose', color: 'primary' },
              { id: 2, title: 'Identity', subtitle: 'Who I Am', color: 'primary' },
              { id: 3, title: 'Values/Beliefs', subtitle: 'What Matters', color: 'secondary' },
              { id: 4, title: 'Skills', subtitle: 'Capabilities', color: 'secondary' },
              { id: 5, title: 'Behavior', subtitle: 'Actions', color: 'accent' },
              { id: 6, title: 'Environment', subtitle: 'Context', color: 'accent' }
            ].map((level) => (
              <div key={level.id} className={`p-3 rounded border-l-4 ${getColorClasses(level.color)}`}>
                <div className="text-sm font-medium">{level.id}. {level.title}</div>
                <div className="text-xs opacity-75">{level.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Insights</h3>
          <div className="space-y-3">
            <div className="p-3 bg-sage-50 border border-sage-200">
              <div className="text-sm font-medium text-sage-800">Hierarchical Influence</div>
              <div className="text-xs text-sage-700">Higher levels influence lower levels</div>
            </div>
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="text-sm font-medium text-[var(--mustard)]">High Learning Value</div>
              <div className="text-xs text-[var(--mustard)]">Deep transformation framework</div>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="text-sm font-medium text-charcoal-900 mb-2">Visual Signatures</h4>
            <div className="flex items-center gap-2">
              <div className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs flex items-center gap-1">
                <Layers className="h-3 w-3" />
                <span>Framework</span>
              </div>
              <div className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-xs flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span>Coaching</span>
              </div>
              <div className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs flex items-center gap-1">
                <Target className="h-3 w-3" />
                <span>Individual</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLogicalPDF = () => (
    <div className="w-full h-[800px] bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8 shadow-lg overflow-y-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Layers className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-3xl font-bold text-charcoal-900">Logical Levels of Change</h1>
        </div>
        <p className="text-lg text-charcoal-600">A Framework for Transformation</p>
        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="px-3 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded-full text-sm flex items-center gap-1">
            <Layers className="h-4 w-4" />
            <span>Framework</span>
          </div>
          <div className="px-3 py-1 bg-sage-100 text-sage-800 rounded-full text-sm flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span>Coaching</span>
          </div>
          <div className="px-3 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-sm flex items-center gap-1">
            <Target className="h-4 w-4" />
            <span>Individual</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">The Framework</h2>
          <div className="space-y-3">
            {[
              { id: 1, title: 'Spirituality', subtitle: 'Mission/Purpose', description: 'Connection to higher purpose', color: 'primary' },
              { id: 2, title: 'Identity', subtitle: 'Who I Am', description: 'Core sense of self', color: 'primary' },
              { id: 3, title: 'Values/Beliefs', subtitle: 'What Matters', description: 'What drives decisions', color: 'secondary' },
              { id: 4, title: 'Skills', subtitle: 'Capabilities', description: 'What I can do', color: 'secondary' },
              { id: 5, title: 'Behavior', subtitle: 'Actions', description: 'What I do', color: 'accent' },
              { id: 6, title: 'Environment', subtitle: 'Context', description: 'Where I operate', color: 'accent' }
            ].map((level) => (
              <div key={level.id} className={`p-4  border-2 ${getColorClasses(level.color)}`}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[var(--bg-raised)] border-2 border-charcoal-600 rounded-full flex items-center justify-center text-xs font-bold text-charcoal-600">
                    {level.id}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{level.title}</div>
                    <div className="text-xs opacity-75">{level.subtitle}</div>
                    <div className="text-xs opacity-60">{level.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-charcoal-900 mb-4">M3 Metadata</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Categories</h3>
              <div className="flex flex-wrap gap-1">
                {['Framework', 'Model', 'Tool'].map((category) => (
                  <span key={category} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                    {category}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Attributes</h3>
              <div className="space-y-2">
                {[
                  { label: 'Impact/Value', value: 9, max: 10 },
                  { label: 'Learning Value', value: 9, max: 10 },
                  { label: 'Clarity/Ambiguity', value: 6, max: 10 }
                ].map((attr) => (
                  <div key={attr.label} className="flex items-center gap-2">
                    <span className="text-xs w-20">{attr.label}</span>
                    <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2">
                      <div 
                        className="bg-consulting-slate-blue-600 h-2 rounded-full"
                        style={{ width: `${(attr.value / attr.max) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs w-4">{attr.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">When to Use</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• Understanding change at different levels</li>
              <li>• Planning transformation initiatives</li>
              <li>• Identifying root causes of issues</li>
              <li>• Coaching and personal development</li>
            </ul>
          </div>
          <div>
            <h3 className="text-md font-medium text-charcoal-700 mb-2">Key Questions</h3>
            <ul className="text-sm text-charcoal-600 space-y-1">
              <li>• At what level is the real issue?</li>
              <li>• What level of change is needed?</li>
              <li>• How do higher levels influence lower ones?</li>
              <li>• Where should I focus my energy?</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderArtifact = () => {
    if (selectedTool === 'ladder') {
      switch (selectedFormat) {
        case 'field-card': return renderLadderFieldCard();
        case 'slide': return renderLadderSlide();
        case 'pdf': return renderLadderPDF();
      }
    } else if (selectedTool === 'johari') {
      switch (selectedFormat) {
        case 'field-card': return renderJohariFieldCard();
        case 'slide': return renderJohariSlide();
        case 'pdf': return renderJohariPDF();
      }
    } else if (selectedTool === 'logical') {
      switch (selectedFormat) {
        case 'field-card': return renderLogicalFieldCard();
        case 'slide': return renderLogicalSlide();
        case 'pdf': return renderLogicalPDF();
      }
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <FileText className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Artifact Gallery</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-4xl mx-auto">
          See how Peak Priorities' coaching tools appear as actual artifacts - field cards, 
          PowerPoint slides, and PDF one-pagers with M3 metadata and visual signatures.
        </p>
      </div>

      {/* Tool and Format Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Tool & Format Selection</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Tool</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'ladder', name: 'Ladder of Inference', icon: <Layers className="h-4 w-4" /> },
                { id: 'johari', name: 'Johari Window', icon: <Users className="h-4 w-4" /> },
                { id: 'logical', name: 'Logical Levels', icon: <Layers className="h-4 w-4" /> }
              ].map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => setSelectedTool(tool.id as any)}
                  className={`p-3  border-2 transition-all text-left ${
                    selectedTool === tool.id
                      ? 'border-consulting-slate-blue-600 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {tool.icon}
                    <span className="text-sm font-medium">{tool.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Format</h3>
            <div className="space-y-2">
              {[
                { id: 'field-card', name: 'Field Card', icon: <CreditCard className="h-4 w-4" /> },
                { id: 'slide', name: 'PowerPoint Slide', icon: <Presentation className="h-4 w-4" /> },
                { id: 'pdf', name: 'PDF One-Pager', icon: <FileText className="h-4 w-4" /> }
              ].map((format) => (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id as any)}
                  className={`p-3  border-2 transition-all text-left w-full ${
                    selectedFormat === format.id
                      ? 'border-sage-600 bg-sage-50 text-sage-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {format.icon}
                    <span className="text-sm font-medium">{format.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Artifact Display */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">
          {selectedTool === 'ladder' ? 'Ladder of Inference' : 
           selectedTool === 'johari' ? 'Johari Window' : 'Logical Levels'} - {selectedFormat === 'field-card' ? 'Field Card' : selectedFormat === 'slide' ? 'PowerPoint Slide' : 'PDF One-Pager'}
        </h2>
        <div className="flex justify-center">
          {renderArtifact()}
        </div>
      </div>

      {/* Download Options */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Download Options</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { format: 'field-card', name: 'Field Card', description: 'Compact reference for coaching prompts' },
            { format: 'slide', name: 'PowerPoint Slide', description: 'Presentation-ready format' },
            { format: 'pdf', name: 'PDF One-Pager', description: 'Comprehensive reference document' }
          ].map((option) => (
            <div key={option.format} className="p-4 border-2 border-[var(--rule)] text-center">
              <Download className="h-8 w-8 text-consulting-slate-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-charcoal-900 mb-1">{option.name}</h3>
              <p className="text-sm text-charcoal-600 mb-3">{option.description}</p>
              <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white text-sm hover:bg-consulting-slate-blue-700 transition-colors">
                Download {option.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
